import { useCallback, useEffect, useRef, useState } from 'react';
import { dailyJournalNoteId, humanizeApiError, type BillPayDraft, type CapabilityManifest } from '@lapo/core';
import type { AssistantMessage, ToastInput } from '@lapo/ui';
import type { ApiClient, AssistantResultDTO } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';

/**
 * Owns the ✦ assistant palette: whether it's open, the in-thread transcript, the busy/error flags, the
 * per-user capability manifest, and every action the palette drives — send a natural-language prompt
 * (`api.assist`), send a staged bill image (`api.assistBillImage`), confirm a drafted bill payment
 * (`api.confirmBillPay`, polling `billStatus` to a terminal state), Stop the in-flight request, Clear the
 * conversation (`api.assistClear`), rehydrate the server-side thread on every open (`api.assistThread`),
 * and load the capability manifest (`api.getCapabilities`). The DIAL voice-call watcher (poll
 * `api.voiceCallStatus` and drop the terminal outcome line into the transcript) lives here too, since it
 * only writes back into the assistant transcript.
 *
 * Behavior-preserving extraction from App.tsx. The one thing that CAN'T live here is the cross-cluster
 * tool dispatch inside `onAssist`: an `add_task` result refreshes Today, a `create_shipping_label` /
 * `track_package` result drops a draft into the Shipments drawer and jumps the right drawer there. Those
 * touch OTHER clusters (Todoist, Shipments) and App's right-drawer layout, so App supplies them as named
 * callbacks — `onAddedTask`, `onShipmentDraft`, `onTrackedPackage` — which the hook invokes at the exact
 * point the inline code did (same order, same `setAssistantOpen(false)` step-out). App wires them to its
 * destructured hook loaders (`loadTodoistToday`, `setShipDraft`, `loadTracked`) plus its drawer routing.
 *
 * Deps are passed in (never reached out of App): `api`, the auth `session` (unused directly today but
 * kept for parity with the other extracted clusters + future gating), `pushToast`, and the three
 * tool-side-effect callbacks. `useFocusTrap` on the popover stays in App (it needs App's popover ref);
 * the hook exposes `assistantOpen` / `setAssistantOpen` for that and for the toolbar ✦ toggle + Esc.
 */
export interface UseAssistantDeps {
  api: ApiClient;
  session: AuthSession | null;
  pushToast: (t: ToastInput) => number;
  /** A `add_task` tool result landed — App refreshes Today (`loadTodoistToday`). */
  onAddedTask: () => void;
  /** A `create_shipping_label` result produced a priced draft — App drops it into the Shipments drawer
   *  and jumps the right drawer there (`setShipDraft` + right-view routing). The palette closes after. */
  onShipmentDraft: (draft: NonNullable<AssistantResultDTO['shipping']>) => void;
  /** A `track_package` result landed — App pulls the new tracked package in and jumps to Shipments
   *  (`loadTracked` + right-view routing). The palette closes after. */
  onTrackedPackage: () => void;
}

export interface UseAssistant {
  assistantOpen: boolean;
  setAssistantOpen: React.Dispatch<React.SetStateAction<boolean>>;
  assistantMessages: AssistantMessage[];
  assistantBusy: boolean;
  /** OPENCLAW streaming — the live one-line progress status ("Searching your email…") while busy. */
  assistantStatus: string | undefined;
  assistantError: string | undefined;
  assistantCapabilities: CapabilityManifest | undefined;
  onAssist: (prompt: string) => Promise<void>;
  onAssistBillImage: (file: File, prompt?: string) => Promise<void>;
  onConfirmBillPay: (billId: string) => Promise<void>;
  onAssistStop: () => void;
  onAssistClear: () => void;
}

/** OPENCLAW streaming — humanize a tool name into the palette's live status line. */
export function toolStatusLabel(tool: string): string {
  const labels: Record<string, string> = {
    search_notes: 'Searching your notes…',
    answer_from_notes: 'Reading your notes…',
    lookup_entity: 'Looking that up…',
    search_email: 'Searching your email…',
    read_email: 'Reading an email…',
    archive_email: 'Archiving the email…',
    draft_email: 'Writing a draft…',
    list_tasks: 'Checking your tasks…',
    add_task: 'Adding the task…',
    add_note: 'Saving the note…',
    get_calendar: 'Checking your calendar…',
    list_shipments: 'Checking your shipments…',
    package_status: 'Checking the package…',
    track_package: 'Setting up tracking…',
    draft_shipping_label: 'Pricing the label…',
    list_initiatives: 'Reading your initiatives…',
    list_decisions: 'Reading your decisions…',
    latest_digest: 'Pulling the digest…',
    call_history: 'Checking call history…',
    today_signals: 'Checking today’s signals…',
    autonomy_recap: 'Reviewing what Lapo handled…',
    finance_overview: 'Reading your finances…',
    query_transactions: 'Searching transactions…',
    list_bills: 'Checking your bills…',
    pay_bill: 'Drafting the payment…',
    add_bill: 'Adding the bill…',
    latest_fitness_summary: 'Reading your fitness data…',
    amazon_cart: 'Checking your cart…',
    amazon_orders: 'Checking your orders…',
    search_amazon_products: 'Searching Amazon…',
    draft_amazon_order: 'Drafting the order…',
    draft_call: 'Preparing the call…',
    place_call: 'Dialing…',
    summarize_podcasts: 'Starting podcast summaries…',
    remember: 'Remembering that…',
    forget: 'Forgetting that…',
    list_memories: 'Checking my memory…',
    memory_search: 'Searching my memory…',
    note_to_self: 'Making a note…',
    run_code: 'Computing…',
    relationship_context: 'Checking the relationship…',
    sender_policy: 'Checking triage rules…',
    list_capabilities: 'Checking what I can do…',
  };
  return labels[tool] ?? 'Working…';
}

export function useAssistant({
  api,
  session: _session,
  pushToast,
  onAddedTask,
  onShipmentDraft,
  onTrackedPackage,
}: UseAssistantDeps): UseAssistant {
  // Assistant palette (✦): route one natural-language line to an action. The server does the work
  // and returns what it did; here we echo the turn and apply the side effect — refresh Today for a
  // new task, drop a shipping draft into the Shipments panel (and jump there to buy), or just confirm
  // the saved note (the sync registry surfaces it in the sidebar on its own).
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>([]);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [assistantStatus, setAssistantStatus] = useState<string | undefined>(undefined);
  const [assistantError, setAssistantError] = useState<string | undefined>(undefined);
  // NEXUS-07: the per-user capability manifest, shown in the assistant's empty state ("what Lapo can do").
  const [assistantCapabilities, setAssistantCapabilities] = useState<CapabilityManifest | undefined>(undefined);
  const assistantSeq = useRef(0);
  // In-flight assistant request, so the ✦ Stop button (and Clear) can cancel it.
  const assistantAbortRef = useRef<AbortController | null>(null);

  // DIAL: a placed call's result arrives asynchronously (Vapi webhook → server thread append) and the
  // palette has no realtime channel — poll the call until terminal, then drop the same outcome line the
  // server pushed to Telegram into the open transcript. Without this the palette says "Calling now…"
  // forever and the user only ever hears back on Telegram.
  const voicePollRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (voicePollRef.current) window.clearInterval(voicePollRef.current);
    },
    [],
  );
  const watchVoiceCall = useCallback(
    (callId: string) => {
      if (voicePollRef.current) window.clearInterval(voicePollRef.current);
      const startedAt = Date.now();
      const stop = (): void => {
        if (voicePollRef.current) window.clearInterval(voicePollRef.current);
        voicePollRef.current = null;
      };
      voicePollRef.current = window.setInterval(() => {
        // Calls are minutes, not hours — a webhook that never lands shouldn't poll forever.
        if (Date.now() - startedAt > 15 * 60_000) return stop();
        void api
          .voiceCallStatus(callId)
          .then((s) => {
            if (s.state === 'dialing' || s.state === 'in-progress') return;
            stop();
            setAssistantMessages((prev) => [
              ...prev,
              {
                id: `m${assistantSeq.current++}`,
                role: 'assistant',
                text: s.outcomeLine ?? `📞 Call to ${s.targetName} ${s.state}.`,
                tool: 'none',
                ok: s.outcome?.succeeded ?? s.state === 'completed',
              },
            ]);
          })
          .catch(() => {});
      }, 4000);
    },
    [api],
  );

  const onAssist = useCallback(
    async (prompt: string) => {
      const userId = `m${assistantSeq.current++}`;
      setAssistantError(undefined);
      setAssistantMessages((prev) => [...prev, { id: userId, role: 'user', text: prompt }]);
      setAssistantBusy(true);
      const controller = new AbortController();
      assistantAbortRef.current = controller;
      // Client-local day so a saved note lands in the journal the user is looking at as "today"
      // (matches the journal note ids the sidebar builds — see dailyJournalNoteId usage above).
      const dateKey = dailyJournalNoteId(new Date()).replace('journal-', '');
      try {
        // OPENCLAW streaming — narrate the agent's tool work live. A pre-streaming server (404) or a
        // client without the method falls back to the plain request so nothing breaks mid-deploy.
        let res: AssistantResultDTO | undefined;
        if (typeof api.assistStream === 'function') {
          try {
            res = await api.assistStream(
              prompt,
              dateKey,
              (ev) => {
                if (ev.type === 'tool' && ev.tool) setAssistantStatus(toolStatusLabel(ev.tool));
              },
              controller.signal,
            );
          } catch (err) {
            if (controller.signal.aborted) throw err;
            if ((err as { status?: number }).status !== 404) throw err;
          }
        }
        if (!res) res = await api.assist(prompt, dateKey, controller.signal);
        setAssistantMessages((prev) => [
          ...prev,
          { id: `m${assistantSeq.current++}`, role: 'assistant', text: res.message, tool: res.tool, ok: res.ok, billDraft: res.billDraft },
        ]);
        if (res.tool === 'add_task') {
          onAddedTask();
        } else if (res.tool === 'create_shipping_label' && res.shipping) {
          onShipmentDraft(res.shipping); // App drops the priced draft in the Shipments drawer + jumps there
          setAssistantOpen(false); // and step out of the way of the assistant popover
        } else if (res.tool === 'track_package') {
          onTrackedPackage(); // App pulls the new tracked package into the Shipments list + jumps there
          setAssistantOpen(false);
        } else if (res.tool === 'place_call' && res.call) {
          watchVoiceCall(res.call.callId); // keep the palette open — the outcome lands in-thread when the call ends
        }
      } catch (err) {
        // A user-initiated Stop aborts the fetch — that's not an error to surface.
        if (controller.signal.aborted || (err as Error).name === 'AbortError') return;
        const message = humanizeApiError(err);
        // Show it once, as a normal in-thread assistant turn — most "errors" are really clarifications
        // ("say how heavy the package is") the user answers next. (Previously this ALSO set a separate
        // red banner, so every failure rendered twice.)
        setAssistantMessages((prev) => [
          ...prev,
          { id: `m${assistantSeq.current++}`, role: 'assistant', text: message, tool: 'none', ok: false },
        ]);
      } finally {
        if (assistantAbortRef.current === controller) assistantAbortRef.current = null;
        setAssistantBusy(false);
        setAssistantStatus(undefined);
      }
    },
    [api, onAddedTask, onShipmentDraft, onTrackedPackage, watchVoiceCall],
  );

  /** REMIT-CHAT — send a message with a staged bill image: upload it (with any typed text), draft a
   * confirm-first payment, echo the turn. The image is staged in the composer and only sent here. */
  const onAssistBillImage = useCallback(
    async (file: File, prompt?: string) => {
      const text = prompt?.trim();
      setAssistantError(undefined);
      setAssistantMessages((prev) => [...prev, { id: `m${assistantSeq.current++}`, role: 'user', text: text ? `📎 ${text}` : '📎 Shared a bill image' }]);
      setAssistantBusy(true);
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const res = await api.assistBillImage(bytes, file.type || 'image/jpeg', text);
        setAssistantMessages((prev) => [
          ...prev,
          { id: `m${assistantSeq.current++}`, role: 'assistant', text: res.message, tool: res.tool, ok: res.ok, billDraft: res.billDraft },
        ]);
      } catch (err) {
        setAssistantMessages((prev) => [
          ...prev,
          { id: `m${assistantSeq.current++}`, role: 'assistant', text: humanizeApiError(err), tool: 'none', ok: false },
        ]);
      } finally {
        setAssistantBusy(false);
      }
    },
    [api],
  );

  /** REMIT-CHAT — the confirm tap on a drafted bill: run the real (guarded) payment, reflect status. */
  const onConfirmBillPay = useCallback(
    async (billId: string) => {
      // Optimistically flip the button to "Paying…" on the matching message.
      const bump = (status: BillPayDraft['status']) =>
        setAssistantMessages((prev) =>
          prev.map((m) => (m.billDraft && m.billDraft.billId === billId ? { ...m, billDraft: { ...m.billDraft, status } } : m)),
        );
      // The worker runs elsewhere (Pub/Sub → browser session); poll the bill until it lands so the
      // card resolves to Paid/failed instead of showing "Paying…" forever while a failure signal
      // quietly appears somewhere else. ~4 min ceiling comfortably covers a normal payment session.
      const watchOutcome = () => {
        const startedAt = Date.now();
        // First terminal response wins. clearInterval can't cancel callbacks a frozen WebView already
        // queued — on Android, resuming the app flushed ~14 backlogged ticks in one second and every
        // one of them toasted the same failure (live 2026-08-19). The flag makes the extras no-ops.
        let settled = false;
        const timer = window.setInterval(() => {
          if (settled || Date.now() - startedAt > 4 * 60_000) return window.clearInterval(timer);
          void api
            .billStatus(billId)
            .then((b) => {
              if (settled) return;
              if (b.status === 'paid') {
                settled = true;
                window.clearInterval(timer);
                bump('paid');
                pushToast({ tone: 'success', message: 'Bill paid — the receipt is in Today.' });
              } else if (b.status === 'manual' || b.status === 'failed') {
                settled = true;
                window.clearInterval(timer);
                bump('failed');
                pushToast({
                  tone: 'error',
                  title: 'Automatic payment didn’t go through',
                  message: b.lastError ?? 'Lapo couldn’t finish this payment — see Today for the pay-it-yourself card.',
                });
              }
            })
            .catch(() => {});
        }, 5000);
      };
      bump('paying');
      try {
        const out = await api.confirmBillPay(billId);
        bump(out.ok ? 'paying' : 'failed');
        if (out.ok) watchOutcome();
        pushToast(out.ok ? { tone: 'success', message: 'Payment started — you’ll see the receipt shortly.' } : { tone: 'error', message: 'Couldn’t start that payment.' });
      } catch (err) {
        bump('failed');
        pushToast({ tone: 'error', title: 'Payment failed to start', message: humanizeApiError(err) });
      }
    },
    [api, pushToast],
  );

  /** Cancel the in-flight ✦ request (Stop button). */
  const onAssistStop = useCallback(() => {
    assistantAbortRef.current?.abort();
    assistantAbortRef.current = null;
    setAssistantBusy(false);
    setAssistantStatus(undefined);
  }, []);

  /** Wipe the ✦ conversation (the sweep button, or typing "clear") — locally and server-side. */
  const onAssistClear = useCallback(() => {
    assistantAbortRef.current?.abort();
    assistantAbortRef.current = null;
    setAssistantBusy(false);
    setAssistantMessages([]);
    setAssistantError(undefined);
    // Also end the server-side thread so the next line starts with no context. Best-effort: a failure
    // here just leaves stale memory the user can clear again; don't surface it.
    void api.assistClear().catch(() => {});
  }, [api]);

  // Rehydrate the palette from the server-side thread on EVERY open (not just the first), so turns
  // appended server-side while it was closed — a voice-call outcome, a Telegram exchange (the thread is
  // shared across channels) — show up without a full reload. Skipped mid-request so an in-flight
  // exchange isn't clobbered; the server thread is authoritative (it stores both sides of every turn).
  useEffect(() => {
    if (!assistantOpen || assistantBusy) return;
    let cancelled = false;
    void api
      .assistThread()
      .then((turns) => {
        if (cancelled || turns.length === 0) return;
        setAssistantMessages((prev) =>
          // Never shrink the visible transcript: the server trims old turns, and a just-sent local turn
          // may not be persisted yet — only adopt the server copy when it has at least as much.
          turns.length >= prev.length
            ? turns.map((t, i) => ({
                id: `h${i}`,
                role: t.role,
                text: t.text,
                tool: t.role === 'assistant' ? t.tool : undefined,
                ok: t.role === 'assistant' ? t.tool !== 'none' : undefined,
              }))
            : prev,
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [assistantOpen, assistantBusy, api]);

  // NEXUS-07: load the capability manifest once the panel opens, so the empty state shows what Lapo can
  // do for THIS user (the same list the assistant reads). Best-effort — falls back to example prompts.
  useEffect(() => {
    if (!assistantOpen || assistantCapabilities) return;
    let cancelled = false;
    void api
      .getCapabilities()
      .then((caps) => {
        if (!cancelled) setAssistantCapabilities(caps);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [assistantOpen, assistantCapabilities, api]);

  // Esc closes the ✦ assistant popover (works even while the composer has focus).
  useEffect(() => {
    if (!assistantOpen) return;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') setAssistantOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [assistantOpen]);

  return {
    assistantOpen,
    setAssistantOpen,
    assistantMessages,
    assistantBusy,
    assistantStatus,
    assistantError,
    assistantCapabilities,
    onAssist,
    onAssistBillImage,
    onConfirmBillPay,
    onAssistStop,
    onAssistClear,
  };
}
